import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { products, images } from '../../mock/mock';
import { AlmondIcon } from '../AlmondIcon';
import { useContent } from '../../context/ContentContext';

export default function Hero({ onOrder, onPickup }) {
  const showcase = [products[0], products[1], products[2], products[3]]; // 8%, 20%, walnut, hazelnut
  const { content, img } = useContent();
  const t = content.text || {};

  return (
    <section className="relative min-h-[100svh] flex items-end overflow-hidden bg-[#5A5D5B] grain">
      {/* backdrop */}
      <img
        src={img('hero', images.hero)}
        alt="Freshly poured almond milk"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#2E302F]/90 via-[#5A5D5B]/30 to-[#9A9D9B]/10" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#2E302F]/60 to-transparent" />

      {/* rotating badge */}
      <div className="absolute top-24 right-4 sm:top-28 sm:right-12 block">
        <div className="relative w-24 sm:w-32 md:w-36 lg:w-44 h-24 sm:h-32 md:h-36 lg:h-44">
          <svg viewBox="0 0 100 100" className="w-full h-full animate-spin-slow">
            <defs>
              <path
                id="circlePath"
                d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0"
              />
            </defs>
            <text fill="#EDEAE0" fontSize="13.5" letterSpacing="3.8" fontFamily="'DM Sans',sans-serif" fontWeight="600">
              <textPath href="#circlePath" startOffset="1%">
                MADE IN MELBOURNE ·
              </textPath>
            </text>
          </svg>
          <div className="absolute inset-0 flex items-center justify-center lg:hidden">
            <AlmondIcon size={50} className="text-[#D2B489]" />
          </div>
        </div>
      </div>

      <div className="relative w-full max-w-7xl mx-auto px-5 sm:px-8 pb-16 lg:pb-24 pt-40">
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 items-end">
          {/* copy */}
          <div>
            <span className="fade-up fade-up-1 inline-flex items-center gap-2 rounded-full bg-[#EDEAE0]/15 border border-[#EDEAE0]/30 px-3.5 py-1.5 sm:px-6 sm:py-3 text-[#E9EBED] text-[10px] sm:text-base font-semibold tracking-[0.15em] sm:tracking-[0.2em] uppercase">
              <AlmondIcon size={14} className="text-[#D2B489] sm:hidden" />
              <AlmondIcon size={18} className="text-[#D2B489] hidden sm:block" />
              Made Fresh in St Kilda
            </span>
            <h1 className="fade-up fade-up-2 mt-6 text-white leading-[0.9] text-[clamp(3.4rem,9vw,7rem)]" style={{ fontFamily: "'Pinyon Script', cursive" }}>
              {t.heroTitle1 || 'Plant-Based'}
              <br />
              <span className="text-white">{t.heroTitle2 || 'Specialty Milks'}</span>
            </h1>
            <p className="fade-up fade-up-3 mt-6 max-w-md text-white text-lg leading-relaxed">
              {t.heroSub || 'Nut milk made to the highest quality using a large amount of nutritious nuts.'}
            </p>
            <div className="fade-up fade-up-4 mt-9 flex flex-wrap items-center gap-4">
              <Link
                to="/products"
                className="group inline-flex items-center gap-2 rounded-full bg-[#EDEAE0] text-[#2C3A28] px-7 py-4 font-semibold hover:bg-white transition-all hover:-translate-y-0.5"
              >
                Shop the range
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/cafe"
                className="inline-flex items-center gap-2 rounded-full border border-[#EDEAE0]/40 text-[#E9EBED] px-7 py-4 font-semibold hover:bg-[#EDEAE0]/10 transition-colors"
              >
                Visit the café
              </Link>
            </div>
          </div>

          {/* floating bottles */}
          <div className="relative flex items-end justify-center h-[300px] sm:h-[400px] lg:h-[440px] mt-20 sm:mt-12 lg:mt-0">
            {showcase.map((p, i) => {
              const heights = [
                'h-[200px] sm:h-[280px] lg:h-[330px]',
                'h-[250px] sm:h-[350px] lg:h-[420px]',
                'h-[220px] sm:h-[305px] lg:h-[365px]',
                'h-[210px] sm:h-[290px] lg:h-[345px]',
              ];
              const z = ['z-10', 'z-20', 'z-10', 'z-0'];
              const fromRight = showcase.length - 1 - i; // rightmost tilts first
              return (
                <div
                  key={p.id}
                  className="group relative animate-bob"
                  style={{ animationDelay: `${i * 0.5}s`, marginLeft: i ? '-1.75rem' : 0 }}
                >
                  <img
                    src={p.image}
                    alt={`${p.name} ${p.badge}`}
                    className={`${heights[i]} ${z[i]} object-contain animate-pour drop-shadow-[0_25px_35px_rgba(0,0,0,0.4)] transition-[filter] duration-300 group-hover:brightness-110`}
                    style={{ animationDelay: `${fromRight * 0.55}s` }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
